import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Mail, Loader2, ArrowLeft } from "lucide-react";
import api from "@/utils/api";
import { setAdminToken, apiError } from "@/components/admin/adminSession";

function Field({ id, label, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-zinc-400 text-xs">{label}</Label>
      <Input id={id} className="bg-zinc-900 border-zinc-800 h-11" {...props} />
    </div>
  );
}

function LoginForm({ email, setEmail, onSuccess, onSetup }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/admin/auth/login", { email, password });
      setAdminToken(res.data.admin_token);
      onSuccess();
    } catch (err) {
      toast.error(apiError(err, "Sign-in failed"));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
      <Field id="admin-email" label="Admin email" type="email" required value={email}
        onChange={(e) => setEmail(e.target.value)} autoComplete="username" data-testid="admin-login-email" />
      <Field id="admin-password" label="Password" type="password" required value={password}
        onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="admin-login-password" />
      <Button type="submit" disabled={busy} className="w-full btn-primary h-11" data-testid="admin-login-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}Sign in
      </Button>
      <button type="button" onClick={onSetup} className="w-full text-sm text-cyan-400 hover:text-cyan-300"
        data-testid="admin-setup-link">
        First time here or forgot your password? Get a code by email
      </button>
    </form>
  );
}

function SetupForm({ email, setEmail, onSuccess, onBack }) {
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/admin/auth/request-code", { email });
      toast.success(res.data.message);
      setCodeSent(true);
    } catch (err) { toast.error(apiError(err, "Could not send code")); }
    finally { setBusy(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (password.length < 10) return toast.error("Password must be at least 10 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const res = await api.post("/admin/auth/set-password", { email, code, password });
      setAdminToken(res.data.admin_token);
      toast.success(res.data.message);
      onSuccess();
    } catch (err) { toast.error(apiError(err, "Could not save password")); }
    finally { setBusy(false); }
  };

  if (!codeSent) {
    return (
      <form onSubmit={requestCode} className="space-y-4" data-testid="admin-request-code-form">
        <Field id="setup-email" label="Admin email" type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)} data-testid="admin-setup-email" />
        <Button type="submit" disabled={busy} className="w-full btn-primary h-11" data-testid="admin-request-code-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}Email me a 6-digit code
        </Button>
        <BackLink onBack={onBack} />
      </form>
    );
  }
  return (
    <form onSubmit={savePassword} className="space-y-4" data-testid="admin-set-password-form">
      <p className="text-sm text-zinc-400">Enter the code sent to <span className="text-white">{email}</span> and choose a password.</p>
      <Field id="setup-code" label="6-digit code" inputMode="numeric" maxLength={6} required value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="bg-zinc-900 border-zinc-800 h-11 tracking-[0.5em] text-center font-mono"
        data-testid="admin-setup-code" />
      <Field id="setup-password" label="New password (min 10 characters)" type="password" required value={password}
        onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="admin-setup-password" />
      <Field id="setup-confirm" label="Confirm password" type="password" required value={confirm}
        onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="admin-setup-confirm" />
      <Button type="submit" disabled={busy || code.length !== 6} className="w-full btn-primary h-11" data-testid="admin-set-password-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}Save password &amp; sign in
      </Button>
      <button type="button" onClick={requestCode} disabled={busy} className="w-full text-sm text-zinc-400 hover:text-white"
        data-testid="admin-resend-code">Resend code</button>
      <BackLink onBack={onBack} />
    </form>
  );
}

function BackLink({ onBack }) {
  return (
    <button type="button" onClick={onBack} className="w-full text-sm text-zinc-500 hover:text-white flex items-center justify-center gap-1"
      data-testid="admin-back-to-login">
      <ArrowLeft className="w-3 h-3" />Back to sign in
    </button>
  );
}

export function AdminLogin({ onSuccess }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  return (
    <div className="min-h-screen bg-[#050505] grid-pattern flex items-center justify-center px-4" data-testid="admin-login-page">
      <Card className="bg-zinc-950/80 border-zinc-800 max-w-md w-full">
        <CardContent className="p-8">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/15 flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Admin area</h1>
          <p className="text-sm text-zinc-500 mb-6">
            {mode === "login" ? "Restricted. Sign in with your admin password." : "Set up or reset your admin password."}
          </p>
          {mode === "login"
            ? <LoginForm email={email} setEmail={setEmail} onSuccess={onSuccess} onSetup={() => setMode("setup")} />
            : <SetupForm email={email} setEmail={setEmail} onSuccess={onSuccess} onBack={() => setMode("login")} />}
        </CardContent>
      </Card>
    </div>
  );
}
